import { LabSubscribeButton, LabSubscribersOnlyCard } from './LabSubscribe.component'
import { EditionType } from '../photoStudioTypes'
import { FC } from 'react'

import { GenerateButton } from '../common/GenerateButton'
import { LabEnhanceOptions } from './LabEnhanceOptions.component'
import { LabEnhanceSettings } from './labSdk.types'
import { LabPanelShell } from './LabPanelPrimitives.component'
import { LabRunSource } from './labRunSource'
import { isLabEnhanceSettingsValid } from './labSdk.mapping'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useLabRunSourceOptions } from './useLabRunSourceOptions.hook'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

interface LabEnhancePanelProps {
  isDisabled?: boolean
  isProcessingImage?: boolean
  /** `source` is the Generate chevron's pick (After when the main CTA was clicked) */
  onGenerate: (settings: LabEnhanceSettings, source?: LabRunSource) => void
}

/**
 * Enhance panel on the Lab SDK engine (FeatureFlag.ALLOW_PHOTO_STUDIO_LAB_SDK): the Enhance options
 * (`LabEnhanceOptions`) and the Generate CTA — no AI label here: in the One tool view it lives in the Stage panel. The
 * settings live in the Studio context, so they survive a tool switch. Non-subscribers get the same panel locked, under
 * Enhance's own Subscribers only copy.
 */
export const LabEnhancePanel: FC<LabEnhancePanelProps> = ({ isDisabled = false, isProcessingImage = false, onGenerate }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const { labEnhanceSettings: settings, updateLabEnhanceSettings } = useClientPhotoStudio()

  const isValid = isLabEnhanceSettingsValid(settings)
  const isGenerateDisabled = isProcessingImage || isDisabled || !isValid
  const disabledTooltip = !isValid ? t('lab.invalid_no_feature') : t('enhance.disabled_generate')
  const sourceOptions = useLabRunSourceOptions((source) => onGenerate(settings, source))
  const isLocked = !hasWorkspaceActiveSubscription

  return (
    <LabPanelShell
      footer={isLocked
        ? <LabSubscribeButton editionType={EditionType.ENHANCE} />
        : (
          <GenerateButton
            onGenerate={() => onGenerate(settings)}
            sourceOptions={sourceOptions}
            disabled={isGenerateDisabled}
            disabledTooltip={isGenerateDisabled ? disabledTooltip : undefined}
            helpPlacement="none"
          />
        )}
    >

      {isLocked && <LabSubscribersOnlyCard title={t('enhance.non_subscribers_title')} description={t('enhance.non_subscribers_description')} margin="0 0 2.4rem" />}

      <LabEnhanceOptions settings={settings} onChange={updateLabEnhanceSettings} disabled={isDisabled || isLocked} />

    </LabPanelShell>
  )
}
